import React, { Fragment } from 'react'
import gql from 'graphql-tag'
import styled from 'react-emotion'
import withQueryParams from 'components/withQueryParams'
import withCookieAcceptance from 'components/withCookieAcceptance'
import { compose } from 'recompose'
import yup from 'yup'
import { email } from 'utils/validations'
import { Redirect } from 'react-router-dom'
import { Mutation } from 'react-apollo'
import { Grid, Form, SecondaryLink, spacer, typography, color } from 'stylist'
import SmartSecondaryLink from 'components/SmartSecondaryLink'
import serializeErrorMessages from 'utils/serializeErrorMessages'
import * as ReactGA from 'react-ga'

const schema = yup.object().shape({
  email,
  firstName: yup.string().required("First name can't be blank"),
  lastName: yup.string().required("Last name can't be blank"),
  phoneNumber: yup
    .string()
    .required("Phone number can't be blank")
    .test(
      'phoneNumber',
      "Phone number must be at least 7 digits",
      value => value && value.replace(/\D/g, '').length >= 7
    ),
  consent: yup
    .boolean()
    .test(
      'is-accepted',
      'You must agree to the Terms and Conditions',
      value => value
    )
})

const StyledGrid = styled(Grid)`
  height: 100%;
`

const SmallSecondaryLink = styled(SecondaryLink)`
  ${typography('body1Medium')};
`

const Disclaimer = styled.div`
  margin-top: ${spacer(5)};
`

const FinePrint = styled.div`
  color: ${color('slate')};
  margin-top: ${spacer(3)};
  ${typography('body1Medium')};
`

const RouteSignUpIndex = ({ match: { path }, query, setCookieAcceptance }) => (
  query.get('client_id') && query.get('hostname') ? (
    <Mutation
      context={{ decamelizeVariables: false }}
      mutation={gql`
        mutation SignUpMutation($input: SignUpInput!) {
          signUp(input: $input) {
            user {
              id
            }
          }
        }
      `}
    >
      {signUp => (
        <StyledGrid
          gridTemplateRows="auto"
          gridTemplateColumns="400px"
          justifyContent="center"
          alignContent="center"
        >
          <Form
            initialValues={{
              consent: false,
              email: '',
              firstName: '',
              lastName: '',
              phoneNumber: ''
            }}
            validationSchema={schema}
            onSubmit={({ consent, ...attributes }) => {
              if (query.get('client_id') === 'new-intervu') {
                ReactGA.ga(
                  'intervu.send',
                  'event',
                  'intervu-sign-up',
                  'submitted'
                )
              }
              return signUp({
                variables: {
                  input: {
                    ...attributes,
                    hostname: query.get('hostname'),
                    redirectUri: query.get('redirect_uri')
                  }
                }
              })
                .then(() =>
                  window.location.replace(query.get('confirmation_uri'))
                )
                .catch(e => Promise.reject(serializeErrorMessages(e)))
            }}
          >
            <Form.Title>Sign Up</Form.Title>
            <Form.Errors />
            <Form.Group>
              <Form.Label htmlFor="email">Email</Form.Label>
              <Form.Input id="email" type="email" name="email" />
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="firstName">First Name</Form.Label>
              <Form.Input id="firstName" type="text" name="firstName" />
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="lastName">Last Name</Form.Label>
              <Form.Input id="lastName" type="text" name="lastName" />
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="phoneNumber">Phone Number</Form.Label>
              <Form.Input id="phoneNumber" type="tel" name="phoneNumber" formatValue={(value) => {
                return value.replace(/(^\+?(?:[\d()\- ]+)?)(.*)/, '$1');
              }}/>
            </Form.Group>
            <Form.Group>
              <Form.Input
                type="checkbox"
                name="consent"
                label={
                  <Fragment>
                    By signing up, you agree to our{' '}
                    <SmallSecondaryLink
                      external
                      target="_blank"
                      href="https://www.focusvision.com/privacy-statement/"
                    >
                      Privacy Policy
                    </SmallSecondaryLink>{' '}
                    and our{' '}
                    <SmallSecondaryLink
                      external
                      target="_blank"
                      href="https://www.focusvision.com/terms-and-conditions/"
                    >
                      Terms and Conditions
                    </SmallSecondaryLink>
                  </Fragment>
                }
                onChange={e => setCookieAcceptance(e.target.checked)}
              />
            </Form.Group>
            <Form.Group buttons>
              <Form.SubmitButton block kind="primary">
                Sign Up
              </Form.SubmitButton>
            </Form.Group>
          </Form>
          <Disclaimer>
            Already have an account?{' '}
            {query.get('redirect_uri') && (
              <SmartSecondaryLink url={query.get('redirect_uri')}>
                Sign in
              </SmartSecondaryLink>
            )}
          </Disclaimer>
          <FinePrint>
            We collect this information to send you free content, offers, and
            product updates. Visit our recently updated{' '}
            <SmallSecondaryLink
              external
              target="_blank"
              href="https://www.focusvision.com/privacy-statement/"
            >
              Privacy Policy
            </SmallSecondaryLink>{' '}
            for details on how we protect and manage your submitted data.
          </FinePrint>
        </StyledGrid>
      )}
    </Mutation>
  ) : (
    <Redirect to="/" />
  )
)

export default compose(
  withCookieAcceptance,
  withQueryParams
)(RouteSignUpIndex)



// WEBPACK FOOTER //
// ./src/routes/sign-up/index.js